import { ERR_INVALID_DATE } from "#lib/validation.js";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DISPLAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
	month: "2-digit",
	day: "2-digit",
	year: "numeric",
});

const ERR_INVALID_DATE_MONTH = "Please enter a valid month";
const ERR_INVALID_DATE_DAY = "Please enter a valid day";
const ERR_INVALID_DATE_YEAR = "Please enter a valid year";

export interface DateParts {
	year: number;
	month: number;
	day: number;
}

function formatTwoDigits(n: number): string {
	return n.toString().padStart(2, "0");
}

export function parseDateParts(value: string | undefined): DateParts | null {
	if (!value || !DATE_PATTERN.test(value)) {
		return null;
	}
	const parts = value.split("-");
	if (parts.length !== 3) {
		return null;
	}
	const year = Number.parseInt(parts[0], 10);
	const month = Number.parseInt(parts[1], 10);
	const day = Number.parseInt(parts[2], 10);
	if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
		return null;
	}
	return { year, month, day };
}

export function isCompleteDate(value: string | undefined): boolean {
	const parts = parseDateParts(value);
	if (!parts) {
		return false;
	}
	return parts.year !== 0 && parts.month !== 0 && parts.day !== 0;
}

function lastDayOfMonth(year: number, month: number): number {
	// Build the date in UTC so the result is a pure calendar fact, independent of the local offset.
	return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function validateMonth(month: number): boolean {
	if (Number.isNaN(month)) {
		return false;
	}
	return month >= 1 && month <= 12;
}

function validateDay(year: number, month: number, day: number): boolean {
	if (Number.isNaN(day)) {
		return false;
	}
	return day >= 1 && day <= lastDayOfMonth(year, month);
}

function validateYear(year: number): boolean {
	if (Number.isNaN(year)) {
		return false;
	}
	const today = new Date();
	return year >= 1900 && year <= today.getFullYear();
}

export function validateDate(value: string | undefined): string | undefined {
	const trimmed = value?.trim() ?? "";
	if (trimmed === "") {
		return undefined;
	}

	const parts = parseDateParts(trimmed);
	if (!parts) {
		return ERR_INVALID_DATE;
	}

	if (!validateMonth(parts.month)) {
		return ERR_INVALID_DATE_MONTH;
	}
	if (!validateDay(parts.year, parts.month, parts.day)) {
		return ERR_INVALID_DATE_DAY;
	}
	if (!validateYear(parts.year)) {
		return ERR_INVALID_DATE_YEAR;
	}

	return undefined;
}

export function dateFromParts(year: number, month: number, day: number): string {
	return `${year}-${formatTwoDigits(month)}-${formatTwoDigits(day)}`;
}

export function dateFromTimestamp(ms: number): string {
	const date = new Date(ms);
	return dateFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function todayDate(): string {
	return dateFromTimestamp(Date.now());
}

export function timestampFromDate(value: string | undefined): number | null {
	const parts = parseDateParts(value);
	if (!parts || !isCompleteDate(value)) {
		return null;
	}
	return new Date(parts.year, parts.month - 1, parts.day).getTime();
}

export function formatDateForDisplay(value: string | undefined): string {
	const parts = parseDateParts(value);
	if (!parts || !isCompleteDate(value)) {
		return "";
	}
	const date = new Date(parts.year, parts.month - 1, parts.day);
	return DISPLAY_FORMATTER.format(date);
}

export function localeMonthAndDay(date: Date, locales?: Intl.LocalesArgument) {
	return date.toLocaleString(locales, { month: "short", day: "numeric" });
}
